import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { API_BASE } from '../config';
import logger from '../utils/logger';
import { setAuthToken, clearAuthToken, getOtpUserInfo, clearOtpUserInfo } from '../utils/authStorage';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backendAvailable, setBackendAvailable] = useState(true);
  const heartbeatIntervalRef = useRef(null);

  const sendHeartbeat = useCallback(async () => {
    if (!user || !userInfo?.is_approved) {
      logger.debug('Heartbeat skipped');
      return;
    }
    
    try {
      const token = await user.getIdToken();
      logger.debug('Sending heartbeat for user:', userInfo.email);
      
      const response = await fetch(`${API_BASE}/user/ping`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        await response.json(); // Response received successfully
        logger.debug('Heartbeat sent successfully');
      } else {
        logger.warn('Heartbeat failed with status:', response.status);
        const errorText = await response.text();
        logger.warn('Heartbeat error:', errorText);
      }
    } catch (error) {
      logger.warn('Heartbeat failed:', error);
    }
  }, [user, userInfo]);

  const startHeartbeat = useCallback(() => {
    logger.debug('Starting heartbeat for user:', userInfo?.email);
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    // Send heartbeat every 2 minutes
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, 2 * 60 * 1000);
    
    // Send initial heartbeat immediately
    sendHeartbeat();
  }, [userInfo?.email, sendHeartbeat]);

  const stopHeartbeat = () => {
    logger.debug('Stopping heartbeat');
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  };

  const fetchUserInfo = async (firebaseUser) => {
    try {
      const token = await firebaseUser.getIdToken();
      logger.debug('Fetching user info for:', firebaseUser.email);
      
      const statusRes = await fetch(`${API_BASE}/user/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (statusRes.ok) {
        const userData = await statusRes.json();
        logger.debug('User status received');
        setUserInfo(userData);
        setBackendAvailable(true);
        
        if (userData.is_approved) {
          logger.debug('User is approved, fetching full user data...');
          try {
            const userRes = await fetch(`${API_BASE}/user/me`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (userRes.ok) {
              const fullUserData = await userRes.json();
              logger.debug('Full user data received');
              setUserInfo({ ...userData, ...fullUserData });
            }
          } catch (userError) {
            logger.warn('Failed to fetch full user data:', userError);
          }
        } else {
          logger.debug('User is not approved yet');
        }
        
        return userData;
      }

      // Backend responded; this is not an offline server.
      setBackendAvailable(true);
      logger.warn('Backend returned error:', statusRes.status, statusRes.statusText);
      if (statusRes.status === 401) {
        setUserInfo(null);
      } else if (statusRes.status === 403) {
        setUserInfo({ email: firebaseUser.email, is_approved: false });
      } else {
        setUserInfo(null);
      }
    } catch (error) {
      logger.error('Failed to fetch user info:', error);
      setBackendAvailable(false);
      setUserInfo(null);
    }
    return null;
  };

  // Set user online in backend
  const setUserOnline = async (firebaseUser) => {
    try {
      // Force refresh the token to get a fresh one
      const token = await firebaseUser.getIdToken(true);
      const response = await fetch(`${API_BASE}/user/online`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        logger.warn('Failed to set user online:', response.status, response.statusText);
        return;
      }
      
      logger.debug('User set as online in backend');
    } catch (err) {
      logger.warn('Failed to set user online:', err);
    }
  };

  // Set user offline in backend
  const setUserOffline = async (firebaseUser) => {
    try {
      // Get token without forcing refresh to avoid issues during logout
      const token = await firebaseUser.getIdToken(false);
      
      const response = await fetch(`${API_BASE}/user/offline`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        logger.debug('User set as offline in backend');
      } else {
        logger.warn('Failed to set user offline:', response.status, response.statusText);
      }
    } catch (err) {
      logger.warn('Failed to set user offline:', err);
    }
  };

  // Effect to handle heartbeat and online status when userInfo changes
  useEffect(() => {
    if (user && userInfo?.is_approved) {
      logger.debug('User is approved, starting heartbeat for:', userInfo.email);
      setUserOnline(user); // Set online in backend
      startHeartbeat();
    } else {
      logger.debug('User not approved or logged out, stopping heartbeat');
      stopHeartbeat();
    }
  }, [user, userInfo, startHeartbeat]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      logger.debug('Auth state changed:', firebaseUser ? 'User logged in' : 'User logged out');
      setUser(firebaseUser);
      if (firebaseUser) {
        const token = await firebaseUser.getIdToken();
        setAuthToken(token);
        await fetchUserInfo(firebaseUser);
      } else {
        clearAuthToken();
        // Only use OTP user info fallback in development (not production)
        const isProduction = process.env.NODE_ENV === 'production';
        
        if (!isProduction) {
          // Development: Check if there's OTP login user info (for local dev without Firebase)
          const userData = getOtpUserInfo();
          if (userData) {
            setUserInfo(userData);
            logger.debug('Using OTP login user info (development mode only)');
          } else {
            setUserInfo(null);
          }
        } else {
          clearOtpUserInfo();
          setUserInfo(null);
        }
        setBackendAvailable(true);
        stopHeartbeat();
      }
      setLoading(false);
    });
    
    // Listen for OTP login success event (development only)
    const handleOtpLogin = () => {
      const isProduction = process.env.NODE_ENV === 'production';
      if (isProduction) {
        // In production, ignore OTP login events (should use Firebase)
        return;
      }
      
      const userData = getOtpUserInfo();
      if (userData) {
        setUserInfo(userData);
        setLoading(false);
        logger.debug('OTP login user info loaded (development mode only)');
      }
    };
    
    // Only listen for OTP login events in development
    if (process.env.NODE_ENV !== 'production') {
      window.addEventListener('otp_login_success', handleOtpLogin);
      // Check for existing OTP user info on mount (development only)
      handleOtpLogin();
    }
    
    return () => {
      unsubscribe();
      if (process.env.NODE_ENV !== 'production') {
        window.removeEventListener('otp_login_success', handleOtpLogin);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup heartbeat on unmount
  useEffect(() => {
    return () => {
      stopHeartbeat();
    };
  }, []);

  const logout = async () => {
    stopHeartbeat();
    if (user) {
      await setUserOffline(user); // Set offline in backend before logout
    }
    await signOut(auth);
    setUser(null);
    setUserInfo(null);
    clearAuthToken();
    clearOtpUserInfo();
  };

  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    const logoutIfAway = () => {
      if (!user && !userInfo) return;
      if (document.visibilityState === 'hidden'){
        logoutRef.current();
      }
    };

    const logoutOnWindowSwitch = () => {
      if (!user && !userInfo) return;
      logoutRef.current();
    };
    document.addEventListener('visibilitychange', logoutIfAway);
    window.addEventListener('blur', logoutOnWindowSwitch);

    return () => {
      document.removeEventListener('visibilitychange', logoutIfAway);
      window.removeEventListener('blur', logoutOnWindowSwitch);
    };
  }, [user, userInfo]);

  const refreshUserInfo = async () => {
    if (user) {
      await fetchUserInfo(user);
    }
  };

  // Manual heartbeat trigger for testing
  const triggerHeartbeat = async () => {
    logger.debug('Manual heartbeat trigger called');
    await sendHeartbeat();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      userInfo, 
      loading, 
      logout, 
      refreshUserInfo,
      backendAvailable,
      triggerHeartbeat // Expose for testing
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext); 